// Submission snapshots describe an existing Morpheus occurrence. They never
// complete, reopen or change the server-owned duty or its source count.
export const isMorpheusField = (field) =>
  field?.completionSource === "morpheus" || field?.type === "morpheus_count";

export function morpheusTimeMillis(value) {
  if (value == null) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") {
    return value.seconds * 1000 + Number(value.nanoseconds || 0) / 1000000;
  }
  const millis = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(millis) ? millis : 0;
}

export function morpheusDayKey(value = new Date()) {
  const millis = morpheusTimeMillis(value);
  if (!millis) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(millis));
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export const morpheusOccurrenceComplete = (row) =>
  row?.completionSource === "morpheus" && (row.completed === true || row.status === "completed");

export function findMorpheusOccurrence(sectionId, field, rows = [], dayKey = morpheusDayKey()) {
  if (!sectionId || !field?.dutyId) return null;
  return rows.find((row) =>
    row.completionSource === "morpheus" &&
    String(row.staffDashboardSectionId || row.standardId || "") === String(sectionId) &&
    String(row.dutyId || "") === String(field.dutyId) &&
    String(row.dayKey || "") === dayKey
  ) || null;
}

export function morpheusResultForField(field, submission) {
  const result = submission?.morpheusCompletions?.[field?.dutyId];
  return result?.dutyId === field?.dutyId ? result : null;
}

export function isMorpheusOccurrenceFiled(row, submission) {
  if (!morpheusOccurrenceComplete(row)) return false;
  // New reports name the exact occurrence. A different occurrence of the same
  // duty, or a count added after that report, is never swallowed by its date.
  if (submission?.morpheusCompletions) {
    const result = morpheusResultForField(row, submission);
    return result?.status === "completed" && result.queueId === row.id;
  }
  // Compatibility for reports submitted before completion snapshots existed.
  const filedAt = morpheusTimeMillis(submission?.at);
  const completedAt = morpheusTimeMillis(row.completedAt);
  return filedAt > 0 && completedAt > 0 && completedAt <= filedAt;
}

export function morpheusCountScheduled(field, dayKey = morpheusDayKey()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return true;
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: "Africa/Johannesburg", weekday: "short",
  }).format(new Date(`${dayKey}T12:00:00+02:00`));
  const recurrence = field?.recurrence || field?.morpheus?.recurrence || { mode: "daily" };
  const weekend = weekday === "Sat" || weekday === "Sun";
  if (recurrence.mode === "weekdays") return !weekend;
  if (recurrence.mode === "weekends") return weekend;
  if (recurrence.mode === "selected_days") return (recurrence.days || []).includes(weekday);
  return true;
}

function snapshotMorpheusResult(field, index, row) {
  return {
    dutyId: String(field.dutyId),
    queueId: String(row.id),
    standardId: String(row.staffDashboardSectionId || row.standardId || ""),
    fieldIndex: index,
    label: String(field.label || row.dutyLabel || "Morpheus count"),
    subheading: String(field.subheading || ""),
    completionSource: "morpheus",
    status: "completed",
    dayKey: String(row.dayKey || ""),
    morpheusSiteId: String(row.morpheusLink?.morpheusSiteId || ""),
    cycleId: String(row.morpheusLink?.cycleId || ""),
    cycleName: String(row.morpheusLink?.cycleName || ""),
    completedAt: morpheusTimeMillis(row.completedAt),
    dueAt: morpheusTimeMillis(row.dueAt),
    completedBy: String(row.completedBy || "Morpheus"),
    completionStatus: String(row.completionStatus || ""),
    sections: (row.morpheusProgress?.sections || []).map((section) => ({
      sectionId: String(section.sectionId || ""),
      sectionName: String(section.sectionName || section.sectionId || ""),
      counted: Number(section.counted || 0),
      total: Number(section.total || 0),
      satisfied: section.satisfied === true,
      sessionId: String(section.sessionId || ""),
    })),
  };
}

export function snapshotSubmissionFields(fields = []) {
  return fields.map((field, index) => ({
    __sourceIndex: index,
    dutyId: String(field.dutyId || field.id || ""),
    label: String(field.label || ""),
    type: String(field.type || ""),
    subheading: String(field.subheading || ""),
    ...(isMorpheusField(field) ? { completionSource: "morpheus" } : {}),
  }));
}

export function fieldsForSubmission(section, submission) {
  return Array.isArray(submission?.submissionFields)
    ? submission.submissionFields
    : section?.fields || [];
}

export function captureMorpheusSubmission(section, rows, previousSubmission, dayKey = morpheusDayKey()) {
  const results = {};
  (section.fields || []).forEach((field, index) => {
    if (!isMorpheusField(field)) return;
    const row = findMorpheusOccurrence(section.id, field, rows, dayKey);
    if (row && isMorpheusOccurrenceFiled(row, previousSubmission)) {
      throw new Error(`${field.label || "This Morpheus count"} has already been submitted. Its next occurrence will appear when scheduled.`);
    }
    if (row && morpheusOccurrenceComplete(row) && morpheusTimeMillis(row.completedAt) > 0) {
      results[field.dutyId] = snapshotMorpheusResult(field, index, row);
    } else if (!row && !morpheusCountScheduled(field, dayKey)) {
      results[field.dutyId] = {
        dutyId: String(field.dutyId), fieldIndex: index,
        label: String(field.label || "Morpheus count"), subheading: String(field.subheading || ""),
        completionSource: "morpheus", status: "not_scheduled", dayKey,
      };
    } else {
      throw new Error(`Complete ${field.label || "this count"} in Morpheus before submitting this section.`);
    }
  });
  return results;
}

// Old submissions omitted source values. Recover only the exact site's duty on
// that submission's operational day, already completed when it was submitted.
// Never use today's count to fill a historical or in-progress report.
export function restoreLegacyMorpheusSubmission(section, submission, rows, storeId) {
  if (submission.__liveDraft || submission.morpheusCompletions) return submission;
  const submittedAt = morpheusTimeMillis(submission.submittedAt || submission.timestamp);
  const dayKey = morpheusDayKey(submittedAt);
  if (!submittedAt || !submission.sectionId || !storeId) return submission;
  const results = {};
  fieldsForSubmission(section, submission).forEach((field, index) => {
    if (!isMorpheusField(field)) return;
    const row = findMorpheusOccurrence(submission.sectionId, field,
      rows.filter((item) => String(item.storeId || "") === String(storeId)), dayKey);
    const completedAt = morpheusTimeMillis(row?.completedAt);
    if (!row || !morpheusOccurrenceComplete(row) || !completedAt || completedAt > submittedAt) return;
    results[field.dutyId] = snapshotMorpheusResult(field,
      Number.isInteger(field.__sourceIndex) ? field.__sourceIndex : index, row);
  });
  return Object.keys(results).length ? { ...submission, morpheusCompletions: results } : submission;
}
